import { useState, useEffect } from 'react';
import YouTube from 'react-youtube';
import { VscPlayCircle } from 'react-icons/vsc';
import VideoOverlay from './VideoOverlay';

interface FeaturedVideoProps {
  heading?: string;
  videoId?: string;
  videoUrl?: string;
  videoTitle?: string;
  industryColor?: string;
  spotlightInView?: boolean;
}

export default function FeaturedVideo({
  heading,
  videoId,
  videoTitle,
  industryColor,
}: FeaturedVideoProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isModalOpen]);

  const YOUTUBE_OPTIONS = {
    height: '100%',
    width: '100%',
    playerVars: {
      autoplay: 1,
      controls: 1,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      mute: 0,
      start: 0,
      fs: 1,
      iv_load_policy: 3,
      disablekb: 0,
      playsinline: 1,
      loop: 1,
      playlist: videoId,
    },
  };

  useEffect(() => {
    const handleClick = () => {
      if (isModalOpen) setIsModalOpen(false);
    };

    if (isModalOpen) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [isModalOpen]);

  const handleVideoClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering close immediately
    setIsModalOpen(true);
  };

  return (
    <>
      <div className='group z-10 col-span-1 flex h-full flex-col justify-between gap-4 pb-4'>
        <h3
          className={`heading-md font-display font-semibold tracking-tighter ${
            industryColor === 'white' || industryColor === 'yellow'
              ? 'text-[#282825]'
              : 'text-white drop-shadow-lg'
          }`}
        >
          {heading}
        </h3>

        {videoId && (
          <>
            <div
              onClick={handleVideoClick}
              className='group relative h-96 w-full cursor-pointer overflow-hidden rounded-2xl'
            >
              <div className='pointer-events-none absolute inset-0 scale-[2] transform'>
                <YouTube
                  videoId={videoId}
                  opts={{
                    ...YOUTUBE_OPTIONS,
                    playerVars: {
                      ...YOUTUBE_OPTIONS.playerVars,
                      controls: 0,
                      mute: 1,
                    },
                  }}
                  className='pointer-events-none h-full w-full'
                />
              </div>
              <div className='absolute bottom-0 w-full p-4'>
                <div className='rounded-xl border border-gray-400 bg-gray-600 bg-opacity-20 p-3 text-xl backdrop-blur-md duration-200 group-hover:bg-opacity-40'>
                  <h3 className='line-clamp-2 flex items-center gap-2 text-white'>
                    {videoTitle} <VscPlayCircle size={30} />
                  </h3>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      {videoId && (
        <VideoOverlay
          videoId={videoId}
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}
